import { useCallback, useMemo } from 'react';
import { IncidentFeedItem, IncidentItem } from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types';
import { ColorHex } from '@/types';
import { INCIDENT_TYPE_COLOR } from '@/utils/getIncidentTypeColor';
import {
  AvailableColorSchemes,
  COLOR_CONFIG_KEYS,
  ColorConfigKey,
  ColorMap,
  DEFAULT_INCIDENT_COLOR_KEY,
} from '../SituationalAwareness.types';
import { useIncidentSchemaKeyStore } from './useIncidentSchemaKeyStore';
import { useIncidentsColorScheme } from './useIncidentsColorScheme';

const isColorConfigKey = (key: string): key is ColorConfigKey => COLOR_CONFIG_KEYS.includes(key as ColorConfigKey);

export const createColorMap = (record: Record<string, ColorHex>): ColorMap => {
  return new Map(Object.entries(record).map(([k, v]) => [String(Math.floor(Number(k))), v])) as ColorMap;
};

type Response = {
  availableSchemes: AvailableColorSchemes | null;
  userSelectedColorMap: ColorMap | null;
  schemaKey: ColorConfigKey | null;
  changeSchema: (key: ColorConfigKey) => void;
  getIncidentColor: (incident: IncidentItem | IncidentFeedItem) => string | undefined;
};

export const useUserIncidentsColorSchemes = (): Response => {
  const { getIncidentSchemaKey, setIncidentSchemaKey } = useIncidentSchemaKeyStore();
  const storedKeyRaw = getIncidentSchemaKey();
  const schemaKey = storedKeyRaw && isColorConfigKey(storedKeyRaw) ? storedKeyRaw : DEFAULT_INCIDENT_COLOR_KEY;

  const { colorSchemes } = useIncidentsColorScheme();

  const availableSchemes = useMemo<AvailableColorSchemes | null>(() => {
    if (!colorSchemes) return null;

    return Object.fromEntries(
      Object.entries(colorSchemes).filter(([k]) => isColorConfigKey(k)),
    ) as AvailableColorSchemes;
  }, [colorSchemes]);

  const userSelectedColorMap = useMemo<ColorMap>(() => {
    const scheme = schemaKey ? availableSchemes?.[schemaKey] : null;
    const defaultMap = createColorMap(scheme || INCIDENT_TYPE_COLOR);
    return defaultMap as ColorMap;
  }, [schemaKey, availableSchemes]);

  const getIncidentColor = useCallback(
    (incident: IncidentItem | IncidentFeedItem) => {
      const key = Math.floor(Number(incident[schemaKey ?? DEFAULT_INCIDENT_COLOR_KEY]));

      return userSelectedColorMap?.get(String(key));
    },
    [userSelectedColorMap, schemaKey],
  );

  return {
    availableSchemes,
    userSelectedColorMap,
    schemaKey,
    changeSchema: setIncidentSchemaKey,
    getIncidentColor,
  };
};
